import PropTypes from 'prop-types';
import React from 'react';
import styled from 'styled-components';

const RadioButtonContainer = styled.div`
  display: flex;
  font-size: 16px;
`;

// Hide radio button visually but remain accessible to screen readers.
// Source: https://polished.js.org/docs/#hidevisually
const RadioInput = styled.input.attrs({ type: 'radio' })`
  border: 0;
  clip: rect(0 0 0 0);
  height: 1px;
  margin: -1px;
  overflow: hidden;
  padding: 0;
  position: absolute;
  white-space: nowrap;
  width: 1px;
`;

const RadioLabel = styled.label`
  margin-bottom: 5px;
  cursor: pointer;
`;

const RadioChildren = styled.span`
  vertical-align: middle;
`;

const RadioOutline = styled.span`
  ${props =>
    props.checked
      ? css`
          border: 2px solid ${props.theme.carvana.blue};
        `
      : css`
          border: 2px solid ${props.theme.carvana.grayThree};
        `}
  border-radius: 15px;
  display: inline-block;
  height: 18px;
  margin-right: 5px;
  position: relative;
  top: 4px;
  width: 18px;
  background-color: ${props => props.theme.carvana.white};
`;

const DotInRadioDot = styled.span`
  border: none;
  border-radius: 15px;
  display: inline-block;
  height: 12px;
  width: 12px;
  margin-left: 3px;
  margin-bottom: 1px;
  background-color: ${props =>
    !props.checked ? props.theme.carvana.white : props.theme.carvana.blue};
`;

const RadioButton = props => {
  const {
    onTouched: ignoredOnTouched,
    validate,
    children,
    name,
    checkedValue,
    ...rest
  } = props;
  const { disabled, value, id, onChange } = rest;
  const checked = value === checkedValue;

  return (
    <RadioButtonContainer>
      <RadioLabel
        checked={value}
        htmlFor={id}
        disabled={disabled}
        data-cv-test={`${value}RadioLabel`}
      >
        <RadioInput
          checked={checked}
          {...rest}
          name={name}
          id={id}
          type="radio"
          value={value}
          onChange={e => onChange(e, validate)}
          data-cv-test={`${value}RadioInput`}
        />
        <RadioOutline checked={checked}>
          <DotInRadioDot checked={checked} />
        </RadioOutline>

        <RadioChildren>{children}</RadioChildren>
      </RadioLabel>
    </RadioButtonContainer>
  );
};

RadioButton.propTypes = {
  onChange: PropTypes.func.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]).isRequired,
  children: PropTypes.node.isRequired,
  name: PropTypes.string.isRequired,
  checkedValue: PropTypes.string,
  onTouched: PropTypes.func,
  text: PropTypes.string,
  disabled: PropTypes.bool,
  color: PropTypes.string,
  uuid: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  validate: PropTypes.func
};

RadioButton.defaultProps = {
  checkedValue: null,
  onTouched: null,
  text: null,
  disabled: false,
  color: null,
  validate: () => {}
};

export default RadioButton;
